import React from "react";
import DataTableActions from "../../../components/common/datatable/DataTableActions";
import i18next from "i18next";
import { EADataGridCell } from "../../../components/common/datatable/EADataGrid";
import { formatDate, formatNumberCurrency, hasOption } from "../../../services/common/Utils";
import InscriptionStatusChip from "../../../components/common/InscriptionStatusChip";
import MainHeaderCell from "../../../components/common/datatable/MainHeaderCell";
import {
  getAttributeValueDisplay,
  getAttributeValueOptions,
  sortAttributeMultipleListValue,
} from "../../../services/common/AttributeUtils";
import { PrivilegeEnum } from "services/common/RolesUtils";
import { DATABLOCKS_SUBSCRIPTIONS } from "services/common/DataBlocksUtils";
import { EvAllScores, EVScorePerfomance, getEcovadisThirdpartyIntegrationID } from "services/common/EcovadisUtils";
import { AttributeType, Subscription } from "services/common/Constants";
import { SecurityRatingAllScoresEnum, SecurityRatingGlobalScoreMaturityEnum, SecurityRatingPerformanceEnum, SecurityRatingScoresEnum } from "services/common/SecurityRatingUtils";
import { missingColor } from "../../../style/globalStyle";
import UnmanagedBadge from "components/thirdparties/unmanagedThirdParty/UnmanagedBadge";
import EAChipWithBackground from "components/EAChipWithBackground";
import { CreditsafeIndicators } from "services/common/CreditsafeUtils";
import { getResultColorCodeById } from '../../../services/common/DataGridUtils';

export const InitialThirdpartiesTableSort = [
  {
    field: "name",
    filterable: false,
    sort: "asc",
  },
];

const getTransparencyScoreDisplay = (value) => {
  if (value) {
    return <span>{value}</span>;
  }
  return <span>{i18next.t("transparency.unpublished")}</span>;
};

const getCreditsafeScoreDisplay = (params) => {
  if (params.field === 'creditsafe_dbt') {
    if (params.value === '-') {
      return <span>-</span>;
    } else if (params.value === 'not_started') {
      <span>{i18next.t('securityRating.messages.not_started')}</span>;
    } else {
      return <span>{params.value + ' ' + i18next.t('creditsafe.days')}</span>;
    }
  } else if (params.field === 'creditsafe_preferentialStatus') {
    return (
      <span>{i18next.t(`creditsafe.preferentialValues.${params.value}`)}</span>
    );
  } else if (params.field === 'creditsafe_creditLimit') {
    const split = params.value.split(' ');
    if (split.length > 1) {
      return <span>{formatNumberCurrency(split[0], split[1])}</span>;
    } else {
      return (
        <span>
          {params.value === 'not_started'
            ? i18next.t('securityRating.messages.not_started')
            : params.value}
        </span>
      );
    }
  }
  return (
    <span>
      {params.value === 'not_started'
        ? i18next.t('securityRating.messages.not_started')
        : params.value}
    </span>
  );
};

const thirdpartiesTableActions = (openPanel, active) => {
  return [
    {
      label: i18next.t(active ? "showModify" : "show"),
      icon: "modify-action",
      isDisabled: false,
      action: () => {
        openPanel();
      },
    },
  ];
};

export const thirdpartiesDefaultVisibilityColumns = (
  attributesColumns,
  categorizedAttributesColumns,
  calculatedAttributesColumns,
  indicatorsColumns,
  active
) => {
  return {
    identifier: false,
    duns: false,
    vat: false,
    thirdpartyCode: false,
    unmanaged: false,
    headquarter: false,
    address: false,
    addressPostCode: false,
    addressCity: false,
    addressCountry: false,
    workforce: false,
    turnover: false,
    registered: false,
    hasRestrictedDisclosure: false,
    indued: false,
    financialStrengthScore: false,
    financialStrengthRating: false,
    esgScore: false,
    esgDepth: false,
    esgRiskEnvironment: false,
    esgRiskSocial: false,
    esgRiskGovernance: false,
    transparencyScore: false,
    ecovadis_integration_id: false,
    ...EvAllScores?.reduce(
      (res, score) => ({
        ...res,
        [`ecovadis_${score}`]: false,
      }),
      {}
    ),
    ...Object.values(SecurityRatingAllScoresEnum)?.reduce(
      (res, score) => ({
        ...res,
        [`security_rating_${score}`]: false,
      }),
      {}
    ),
    ...Object.values(CreditsafeIndicators)?.reduce(
      (res, score) => ({
        ...res,
        [`creditsafe_${score}`]: false,
      }),
      {}
    ),
    complete: active,
    mainActivity: false,
    stoppingDate: true,
    ...attributesColumns?.reduce(
      (res, attr) => ({
        ...res,
        [`attribute-${attr.label}`]: false,
      }),
      {}
    ),
    ...categorizedAttributesColumns?.reduce(
      (res, attr) => ({
        ...res,
        [`attribute-${attr.label}`]: false,
      }),
      {}
    ),
    ...indicatorsColumns?.reduce(
      (res, indic) => ({
        ...res,
        [`indicator-${indic.label}`]: false,
      }),
      {}
    ),
    ...calculatedAttributesColumns?.reduce(
      (res, attr) => ({
        ...res,
        [`calculatedAttribute-${attr.label}`]: false,
      }),
      {}
    ),
  };
};

export const getThirdpartiesTableColumns = (
  attributesColumns,
  categorizedAttributesColumns,
  calculatedAttributesColumns,
  indicatorsColumns,
  active,
  history,
  accountId,
  showTransparency,
  hasAnyOfPrivileges, 
  accounts,
  openPanel,
  sortModel
) => {
  return [
    {
      field: "name",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.name"),
      flex: 0.17,
      renderHeader: () => (
        <MainHeaderCell name={i18next.t("thirdpartiesTable.columns.name")} />
      ),
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', gap: "15px"}}>
            {params.value}
            {params.row.companyIdType && params.row.companyIdType === 'CIT004' && (
              <UnmanagedBadge/>
            )}
          </div>
        </EADataGridCell>
      ),
    },
    {
      field: "brandName",
      filterable: false,
      headerName: i18next.t("thirdpartySheet.brandName"),
      flex: 0.065,
      renderHeader: () => (
        <MainHeaderCell name={i18next.t("thirdpartySheet.brandName")} />
      ),
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "unmanaged",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.unmanaged"),
      flex: 0.065,
      renderHeader: () => (
        <MainHeaderCell name={i18next.t("thirdpartiesTable.columns.unmanaged")} />
      ),
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value ? i18next.t("yes") : i18next.t("no")}
        </EADataGridCell>
      ),
    },
    {
      field: "identifier",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.identifier"),
      flex: 0.1,
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "duns",
      filterable: false,
      headerName: i18next.t("duns"),
      flex: 0.1,
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "vat",
      filterable: false,
      headerName: i18next.t("tva"),
      flex: 0.1,
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "thirdpartyCode",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.thirdpartyCode"),
      flex: 0.08,
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "activity",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.activity"),
      flex: 0.1,
      type: "singleSelect",
      hide: !active,
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "mainActivity",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.major"),
      flex: 0.1,
      type: "singleSelect",
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "headquarter",
      filterable: false,
      headerName: i18next.t("headquarters"),
      flex: 0.07,
      type: "singleSelect",
      valueOptions: [
        { 
          value: "headquarter", 
          label: i18next.t("headquarters") 
        },
        {
          value: "secondaryBranch",
          label: i18next.t("secondaryBranch"),
        },
      ],
      valueFormatter: (params) => i18next.t(params.value),
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {i18next.t(params.value)}
        </EADataGridCell>
      ),
    },
    {
      field: "address",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.address"),
      flex: 0.06,
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "addressPostCode",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.addressPostCode"),
      flex: 0.06,
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "addressCity",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.addressCity"),
      flex: 0.1,
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "addressCountry",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.addressCountry"),
      flex: 0.06,
      type: "singleSelect",
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "turnover",
      filterable: false,
      sortable: false,
      headerName: i18next.t("thirdpartiesTable.columns.turnover"),
      flex: 0.06,
      type: "singleSelect",
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "workforce",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.workforce"),
      flex: 0.06,
      type: "singleSelect",
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {params.value}
        </EADataGridCell>
      ),
    },
    {
      field: "followingDate",
      filterable: false,
      headerName: i18next.t("thirdpartiesTable.columns.onboardDate"),
      flex: 0.08,
      type: "date",
      headerAlign: "center",
      align: "center",
      valueFormatter: (params) => formatDate(params.value),
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
        >
          {formatDate(params.value)}
        </EADataGridCell>
      ),
    },
    ...(!active
      ? [
          {
            field: "stoppingDate",
            filterable: false,
            headerName: i18next.t("thirdpartiesTable.columns.offboardDate"),
            flex: 0.08,
            type: "date",
            headerAlign: "center",
            align: "center",
            valueFormatter: (params) => formatDate(params.value),
            renderCell: (params) => (
              <EADataGridCell
                type={"callBack"}
                callBack={() => openPanel(params.row.id)}
              >
                {formatDate(params.value)}
              </EADataGridCell>
            ),
          },
        ]
      : []),
    {
      field: "registered",
      filterable: false,
      headerName: i18next.t("dossierTable.columns.contact"),
      flex: 0.08,
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: [
        {
          value: "INSCRIT",
          label: i18next.t(`thirdpartiesTable.status.INSCRIT.label`),
        },
        {
          value: "INVITE",
          label: i18next.t(`thirdpartiesTable.status.INVITE.label`),
        },
        {
          value: "SANS CONTACT",
          label: i18next.t(`thirdpartiesTable.status.SANS CONTACT.label`),
        },
        {
          value: "DECLINED",
          label: i18next.t(`thirdpartiesTable.status.DECLINED.label`),
        },
      ],
      valueFormatter: (params) =>
        i18next.t(`thirdpartiesTable.status.${params.value}.label`),
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
          tooltip={false}
        >
          <InscriptionStatusChip registrationStatus={params.value} />
        </EADataGridCell>
      )
    },
    {
      field: "hasRestrictedDisclosure",
      filterable: false,
      headerName: i18next.t("hasRestrictedDisclosure.title"),
      flex: 0.05,
      headerAlign: "center",
      align: "center",
      type: "boolean",
      renderCell: (params) => (
        <EADataGridCell
          type={"callBack"}
          callBack={() => openPanel(params.row.id)}
          tooltip={false}
        >
          {params.value &&
          <EAChipWithBackground label={i18next.t("hasRestrictedDisclosure.title")}
                color={missingColor}
                tooltip={i18next.t("hasRestrictedDisclosure.tooltip")} />
          }
        </EADataGridCell>
      )
    },
    ...(active
      ? [
          ...(hasOption(accounts, accountId, Subscription.INDUED)
          && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE)
            ? [
                {
                  field: "indued",
                  filterable: false,
                  headerName: i18next.t("indued.score"),
                  flex: 0.1,
                  headerAlign: "center",
                  align: "center",
                  type: "singleSelect",
                  valueOptions: [
                    {
                      value: "not_started",
                      label: i18next.t(`indued.severity.not_started`),
                    },
                    { value: "low", label: i18next.t(`indued.severity.low`) },
                    {
                      value: "medium",
                      label: i18next.t(`indued.severity.medium`),
                    },
                    { value: "high", label: i18next.t(`indued.severity.high`) },
                    {
                      value: "unknown",
                      label: i18next.t(`indued.severity.unknown`),
                    },
                  ],
                  valueFormatter: (params) =>
                    i18next.t(`indued.severity.${params.value}`),
                  renderCell: (params) => (
                    <EADataGridCell
                      type={"callBack"}
                      callBack={() => openPanel(params.row.id)}
                    >
                      {i18next.t(`indued.severity.${params.value}`)}
                    </EADataGridCell>
                  ),
                },
              ]
            : []),
          ...(showTransparency &&
          hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE)
            ? [
                {
                  field: "transparencyScore",
                  filterable: false,
                  headerName: i18next.t("transparency.score"),
                  flex: 0.08,
                  headerAlign: "center",
                  align: "center",
                  type: "number",
                  renderCell: (params) => (
                    <EADataGridCell
                      type={"callBack"}
                      callBack={() => openPanel(params.row.id)}
                    >
                      {getTransparencyScoreDisplay(params.value)}
                    </EADataGridCell>
                  ),
                },
              ]
            : []),
          ...(hasOption(accounts, accountId, DATABLOCKS_SUBSCRIPTIONS.FINANCIALSTRENGTH)
                && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE)
            ? [
                {
                  field: "financialStrengthScore",
                  filterable: false,
                  headerName: i18next.t("financialStrength.labelVariant"),
                  flex: 0.1,
                  headerAlign: "center",
                  align: "center",
                  type: "text",
                  renderCell: (params) => (
                    <EADataGridCell
                      type={"callBack"}
                      callBack={() => openPanel(params.row.id)}
                    >
                      {params.value}
                    </EADataGridCell>
                  ),
                },
                {
                  field: "financialStrengthRating",
                  filterable: false,
                  headerName: i18next.t("datablocks.financialStrength.rating"),
                  flex: 0.1,
                  headerAlign: "center",
                  align: "center",
                  type: "text",
                  renderCell: (params) => (
                    <EADataGridCell
                      type={"callBack"}
                      callBack={() => openPanel(params.row.id)}
                    >
                      {params.value}
                    </EADataGridCell>
                  ),
                },
              ]
            : []),
          ...(hasOption(accounts, accountId, DATABLOCKS_SUBSCRIPTIONS.ESG)
            && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE)
              ? [
                  {
                    field: "esgScore",
                    filterable: false,
                    headerName: i18next.t("ESG.labelScore"),
                    flex: 0.1,
                    headerAlign: "center",
                    align: "center",
                    type: "text",
                    renderCell: (params) => (
                      <EADataGridCell
                        type={"callBack"}
                        callBack={() => openPanel(params.row.id)}
                      >
                        {params.value}
                      </EADataGridCell>
                    ),
                  },
                  {
                    field: "esgDepth",
                    filterable: false,
                    headerName: i18next.t("ESG.scoreDepth"),
                    flex: 0.1,
                    headerAlign: "center",
                    align: "center",
                    type: "text",
                    renderCell: (params) => (
                      <EADataGridCell
                        type={"callBack"}
                        callBack={() => openPanel(params.row.id)}
                      >
                        {params.value}
                      </EADataGridCell>
                    ),
                  },
                  {
                    field: "esgRiskEnvironment",
                    filterable: false,
                    headerName: i18next.t("ESG.riskEnvironment"),
                    flex: 0.1,
                    headerAlign: "center",
                    align: "center",
                    type: "text",
                    renderCell: (params) => (
                      <EADataGridCell
                        type={"callBack"}
                        callBack={() => openPanel(params.row.id)}
                      >
                        {params.value}
                      </EADataGridCell>
                    ),
                  },
                  {
                    field: "esgRiskSocial",
                    filterable: false,
                    headerName: i18next.t("ESG.riskSocial"),
                    flex: 0.1,
                    headerAlign: "center",
                    align: "center",
                    type: "text",
                    renderCell: (params) => (
                      <EADataGridCell
                        type={"callBack"}
                        callBack={() => openPanel(params.row.id)}
                      >
                        {params.value}
                      </EADataGridCell>
                    ),
                  },
                  {
                    field: "esgRiskGovernance",
                    filterable: false,
                    headerName: i18next.t("ESG.riskGovernance"),
                    flex: 0.1,
                    headerAlign: "center",
                    align: "center",
                    type: "text",
                    renderCell: (params) => (
                      <EADataGridCell
                        type={"callBack"}
                        callBack={() => openPanel(params.row.id)}
                      >
                        {params.value}
                      </EADataGridCell>
                    ),
                  },
                ]
            : []),
          ...(hasOption(accounts, accountId, Subscription.ECOVADIS)
            && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE)
              ? [
                {
                  field: "ecovadis_integration_id",
                  filterable: false,
                  sortable: false,
                  headerName: i18next.t("ecovadis.integrationId"),
                  flex: 0.1,
                  headerAlign: "center",
                  align: "center",
                  type: "text",
                  renderCell: (params) => (
                    <EADataGridCell
                      type={"callBack"}
                      callBack={() => openPanel(params.row.id)}
                    >
                      {getEcovadisThirdpartyIntegrationID(params.row.thirdpartyId)}
                    </EADataGridCell>
                  ),
                }, ...(EvAllScores?.map((score) => {
                return {
                  field: `ecovadis_${score}`,
                  filterable: false,
                  headerName: "Ecovadis " + i18next.t(`ecovadis.scores.${score}`),
                  flex: 0.08,
                  type: "singleSelect",
                  headerAlign: "center",
                  valueOptions: Object.values(EVScorePerfomance).map(value => ({value, label: i18next.t(`ecovadis.performance.${value}`)})),
                  renderCell: (params) => (
                    <EADataGridCell
                        type={"callBack"}
                        callBack={() => openPanel(params.row.id)}
                      >
                      <span>{i18next.t(`ecovadis.performance.${params.value}`)}</span>
                    </EADataGridCell>
                  ),
                };
              })
            )] : []),
          ...(hasOption(accounts, accountId, Subscription.SECURITY_RATING)
            && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE)
              ? [
                {
                  field: `security_rating_global_score`,
                  filterable: false,
                  headerName: "Security Rating " + i18next.t(`securityRating.scores.global_score`),
                  flex: 0.08,
                  type: "singleSelect",
                  headerAlign: "center",
                  valueOptions: Object.values(SecurityRatingGlobalScoreMaturityEnum).map(value => ({value, label: i18next.t(`securityRating.maturity.${value.toLowerCase()}`)})),
                  renderCell: (params) => (
                    <EADataGridCell
                      type={"callBack"}
                      callBack={() => openPanel(params.row.id)}
                    >
                      <span>
                        {params.value === "not_started" ? 
                          i18next.t("securityRating.messages.not_started") : 
                          i18next.t(`securityRating.maturity.${params.value.toLowerCase()}`)
                        }
                      </span>
                    </EADataGridCell>
                  ),
                },
                ...(Object.values(SecurityRatingScoresEnum)?.map((score) => {
                return {
                  field: `security_rating_${score}`,
                  filterable: false,
                  headerName: "Security Rating " + i18next.t(`securityRating.scores.${score}`),
                  flex: 0.08,
                  type: "singleSelect",
                  headerAlign: "center",
                  valueOptions: Object.values(SecurityRatingPerformanceEnum).map(value => ({value, label: value})),
                  renderCell: (params) => (
                    <EADataGridCell
                      type={"callBack"}
                      callBack={() => openPanel(params.row.id)}
                    >
                      <span>{params.value === "not_started" ? i18next.t("securityRating.messages.not_started") : params.value}</span>
                    </EADataGridCell>
                  ),
                };
              })
            )] : []),
        ...(hasOption(accounts, accountId, Subscription.CREDITSAFE)
          && hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE)
          ? [
            ...(Object.values(CreditsafeIndicators)?.map((score) => {
              return {
                field: `creditsafe_${score}`,
                filterable: false,
                sortable: score !== CreditsafeIndicators.CREDIT_LIMIT,
                headerName: "Creditsafe " + i18next.t(`creditsafe.scores.${score}`),
                flex: 0.08,
                headerAlign: "center",
                type: "text",
                renderCell: (params) => (
                  <EADataGridCell
                    type={"callBack"}
                    callBack={() => openPanel(params.row.id)}
                  >
                    {getCreditsafeScoreDisplay(params)}
                  </EADataGridCell>
                ),
              };
            })
            )
          ]
          : []),
        ]
      : []),
    ...[...(attributesColumns || []), ...(categorizedAttributesColumns || [])]?.map((attrCol) => {
      return {
        field: `attribute-${attrCol.label}`,
        filterable: false,
        headerName: attrCol.label,
        renderHeader: () => <span>{attrCol.label}</span>,
        flex: 0.08,
        type: attrCol.gridColumnType,
        headerAlign: "center",
        ...(attrCol.gridColumnType === "singleSelect" && {
          valueOptions: getAttributeValueOptions(attrCol),
        }),
        ...(attrCol.gridColumnType === "number" && {
          valueFormatter: (params) =>
            getAttributeValueDisplay(attrCol.type, params.value),
        }),
        renderCell: (params) => {
          let attributeValuesStr = getAttributeValueDisplay(attrCol.type, params.value);
          if(attrCol.type === AttributeType.LIST_MULTIPLE) {
            attributeValuesStr = sortAttributeMultipleListValue(attributeValuesStr, params.field, sortModel)
          }
          return (
            <EADataGridCell
              type={"callBack"}
            callBack={() => openPanel(params.row.id)}
            >
              <span>{attributeValuesStr}</span>
            </EADataGridCell>
          )
        },
      };
    }),
    ...calculatedAttributesColumns?.map((attrCol) => {
      return {
        field: `calculatedAttribute-${attrCol.label}`,
        filterable: false,
        headerName: attrCol.label,
        renderHeader: () => <span>{attrCol.label}</span>,
        flex: 0.08,
        headerAlign: "center",
        type: attrCol.gridColumnType,
        renderCell: (params) => {
          const indicators = params.row.calculatedAttributes || [];
          const calculationId = attrCol.calculationId;

          return (
              <EADataGridCell
                  type="callBack"
                  callBack={() => openPanel(params.row.id)}
              >
                  <div
                      style={{
                          color: "#535362",
                          fontSize: "11px",
                          display: "flex",
                          alignItems: "center",
                          backgroundColor : getResultColorCodeById(indicators, calculationId),
                          gap: "5px",
                          padding: "8px",
                          height: "28px",
                          borderRadius: "13px"}}
                  >
                      {params.value}
                  </div>
              </EADataGridCell>
          );
        },
      };
    }),
    ...indicatorsColumns?.map((indicCol) => {
      return {
        field: `indicator-${indicCol.label}`,
        filterable: false,
        headerName: indicCol.label,
        renderHeader: () => <span>{indicCol.label}</span>,
        flex: 0.08,
        headerAlign: "center",
        type: indicCol.gridColumnType,

          renderCell: (params) => {
              const indicators = params.row.indicators || [];
              const calculationId = indicCol.calculationId;

              return (
                  <EADataGridCell
                      type="callBack"
                      callBack={() => openPanel(params.row.id)}
                  >
                      <div
                          style={{
                              color: "#535362",
                              fontSize: "11px",
                              display: "flex",
                              alignItems: "center",
                              backgroundColor: getResultColorCodeById(indicators, calculationId),
                              gap: "5px",
                              padding: "8px",
                              height: "28px",
                              borderRadius: "13px"}}
                      >
                          {params.value}
                      </div>
                  </EADataGridCell>
              );
          },
      };
    }),
    {
      field: "action",
      headerName: i18next.t("actions"),
      flex: 0.05,
      sortable: false,
      filterable: false,
      disableExport: true,
      hideSortIcons: true,
      disableReorder: true,
      disableColumnMenu: true,
      disableMultipleSelection: true,
      disableSelectionOnClick: true,
      isRowSelectable: false,
      hideFooterSelectedRowCount: true,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return (
          <DataTableActions
            actions={thirdpartiesTableActions(
              () => openPanel(params.row.id),
              active
            )}
          />
        );
      },
    },
  ];
};
